import { z } from "zod"

import {
  allArithmeticDrawingOrders,
  allArithmeticOperations,
  allGenericWorksheetModuleTypes,
  allGradeGroups,
  allMathematicsWorksheetModuleTypes,
  allThemes,
} from "../../consts/generator"
import { zodSortEnum } from "../zod"
import { allDatabankThemes } from "./modules/databank"

export const zWorksheetTheme = z.enum(allThemes)

export type WorksheetTheme = z.infer<typeof zWorksheetTheme>

export const zWorksheetSize = z.enum(["half", "full"])

export type WorksheetSize = z.infer<typeof zWorksheetSize>

export const zWorksheetMathematicsModuleType = z.enum(
  allMathematicsWorksheetModuleTypes,
)

export type WorksheetMathematicsModuleType = z.infer<
  typeof zWorksheetMathematicsModuleType
>

export const zWorksheetGenericLanguageModule = z.enum(
  allGenericWorksheetModuleTypes,
)

export type WorksheetGenericLanguageModule = z.infer<
  typeof zWorksheetGenericLanguageModule
>

export const worksheetModuleTypes = [
  ...allGenericWorksheetModuleTypes,
  ...allMathematicsWorksheetModuleTypes,
] as const

export const AllModuleTypes = z.enum(worksheetModuleTypes)

export type AllModuleTypes = z.infer<typeof AllModuleTypes>

export const WorksheetMathematicsModule = z.object({
  type: zWorksheetMathematicsModuleType,
  size: zWorksheetSize,
})

export type WorksheetMathematicsModule = z.infer<
  typeof WorksheetMathematicsModule
>

export const WorksheetModule = z.object({
  // A null value here indicates that a random type can be selected
  type: z.nullable(AllModuleTypes),
  size: zWorksheetSize,
  /**
   * If a seed is provided, it will be used to generate the content of this module.
   * This allows for deterministic generation of a specific module within a worksheet.
   */
  seed: z.string().optional(),
  configuration: z.unknown().optional(),
})

export type WorksheetModule = z.infer<typeof WorksheetModule>

export const GradeGroup = z.enum(allGradeGroups)

export type GradeGroup = z.infer<typeof GradeGroup>

export const allLanguageThemes = allDatabankThemes
export const LanguageTheme = z.enum(allDatabankThemes)
export type LanguageTheme = z.infer<typeof LanguageTheme>

export const CreateWorksheetInput = z.object({
  name: z.string(),
  grade: z.optional(z.number().int().min(1).max(7)),
  gradeGroup: z.optional(GradeGroup),
  subject: z.enum(["MATHEMATICS", "NORWEGIAN", "ENGLISH"]),
  theme: zWorksheetTheme,

  // Only defined for language subject tasks
  languageTheme: z.optional(LanguageTheme),

  // Only defined for math subject tasks
  operation: z.optional(z.enum(allArithmeticOperations)),

  /**
   * Currently this applies for every page. A TODO here is to
   * make it possible to override this.
   */
  sizeConfiguration: zWorksheetSize,
  pages: z.number().int().min(1).max(10),
  /**
   * If a seed is provided, it will be used as the base seed for the worksheet generation.
   * This is useful for regenerating a worksheet with the same layout but potentially different
   * specific module seeds, or for ensuring the "random" layout is consistent.
   */
  seed: z.string().optional(),
  allowedModuleTypes: z.optional(z.array(AllModuleTypes)),
  moduleConfiguration: z.optional(
    z.record(z.string(), z.record(z.string(), WorksheetModule)),
  ),
})

export type CreateWorksheetInput = z.infer<typeof CreateWorksheetInput>

export const GetManyWorksheetsFilterSchema = z.object({
  filterString: z.optional(z.string()),
  skip: z.optional(z.number()),
  take: z.optional(z.number()),
  isFavourite: z.optional(z.boolean()),
  subject: z.optional(z.array(z.enum(["MATHEMATICS", "NORWEGIAN", "ENGLISH"]))),
  orderBy: z.optional(
    z.array(
      z.object({
        name: z.optional(zodSortEnum),
        createdAt: z.optional(zodSortEnum),
      }),
    ),
  ),
})

export type GetManyWorksheetsFilterSchema = z.infer<
  typeof GetManyWorksheetsFilterSchema
>

////////////////////////////////////////////////////
////////////////////////////////////////////////////
/// ====== SPECIFIC MODULE CONFIGURATIONS ====== ///
////////////////////////////////////////////////////
////////////////////////////////////////////////////
export const WordlistConfiguration = z.object({
  wordlist: z.array(z.string()),
  alphabet: z.optional(z.array(z.string())),
})

export type WordlistConfiguration = z.infer<typeof WordlistConfiguration>

export const ArithmeticDrawingOrder = z.enum(allArithmeticDrawingOrders)

export type ArithmeticDrawingOrder = z.infer<typeof ArithmeticDrawingOrder>

export const ArithmeticConfiguration = z.object({
  legalOperations: z.optional(
    z.array(z.enum(["addition", "subtraction", "multiplication", "division"])),
  ),
  legalDrawingOrders: z.optional(z.array(ArithmeticDrawingOrder)),
  min: z.optional(z.number().int()),
  max: z.optional(z.number().int()),
  allowNegativeResult: z.optional(z.boolean()),
  allowTenCrossing: z.optional(z.union([z.boolean(), z.number()])),
})

export type ArithmeticConfiguration = z.infer<typeof ArithmeticConfiguration>

export const MathematicsMinMaxConfiguration = z.object({
  min: z.optional(z.number().int()),
  max: z.optional(z.number().int()),
})

export type MathematicsMinMaxConfiguration = z.infer<
  typeof MathematicsMinMaxConfiguration
>
